import {Component, Input, OnChanges, OnInit, SimpleChanges, ViewChild, ViewContainerRef, ChangeDetectionStrategy} from '@angular/core';
import {ThemeSchema} from '../../../../../models/cms/widget/theme/theme-schema';
import {BorderComponent} from '../style/border/border.component';
import {ThemeSchemaStyle} from '../../../../../models/cms/widget/theme/theme-schema-style';
import {AbstractStyleComponent} from '../style/abstract-style/abstract-style.component';
import {ColorComponent} from '../style/color/color.component';
import {BackgroundComponent} from '../style/background/background.component';
import {FontComponent} from '../style/font/font.component';
import {ButtonComponent} from '../style/button/button.component';
import {InputComponent} from '../style/input/input.component';
import {ShadowComponent} from '../style/shadow/shadow.component';
import {StepComponent} from '../style/step/step.component';
import {StepActiveComponent} from '../style/step-active/step-active.component';
import {WidgetStyleComponent} from '../style/widget-style/widget-style.component';
import {FormStyleComponent} from '../style/form-style/form-style.component';
import {ThemeSchemaGroup} from '../../../../../models/cms/widget/theme/theme-schema-group';
import {GroupComponent} from '../style/group/group.component';

@Component({
  selector: 'app-theme-selector',
  imports: [],
  templateUrl: './theme-selector.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrl: './theme-selector.component.scss'
})
export class ThemeSelectorComponent implements OnInit, OnChanges {
  @Input('schema') schema: ThemeSchema | null = null;
  @Input('theme') theme: any | null = null;

  @ViewChild('container', {read: ViewContainerRef, static: true}) container!: ViewContainerRef;

  styleComponents: Map<string, AbstractStyleComponent> = new Map();

  ngOnInit(): void {
    this.refreshSchema();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['schema']) {
      this.refreshSchema();
    }

    if (changes['theme']) {
      this.refreshStyles();
    }
  }

  refreshSchema(): void {
    this.container.clear();
    this.styleComponents.clear();

    if (this.schema === null) {
      return;
    }

    this.schema.elements.forEach(element => {
      if (element.getType() === 'style') {
        const style = element as ThemeSchemaStyle;
        const component = this.createStyleComponent(style, this.container, 'theme-style-component');
        if (component) {
          this.styleComponents.set(style.key, component);
        }
      }

      if (element.getType() === 'group') {
        const group = element as ThemeSchemaGroup;
        const groupComponent = this.createGroupComponent(group, 'theme-style-component');

        if (groupComponent) {
          group.styles.forEach(style => {
            const styleComponent = this.createStyleComponent(style, groupComponent.container, 'theme-group-style-component');
            if (styleComponent) {
              this.styleComponents.set(style.key, styleComponent);
            }
          });
        }
      }
    });

    setTimeout(() => {
      this.refreshStyles();
    });
  }

  refreshStyles(): void {
    if (this.theme) {
      this.styleComponents.forEach((styleComponent, key) => {
        if (key in this.theme) {
          setTimeout(() => {
            styleComponent.setValue(this.theme[key]);
            this.theme[key] = styleComponent.getValue();
          }, 0);
        }
      });
    }
  }

  private createGroupComponent(group: ThemeSchemaGroup, classes: string = ''): GroupComponent | null {
    const componentRef = this.container.createComponent(GroupComponent);
    const groupComponent = componentRef.instance as GroupComponent;

    groupComponent.label = group.description;

    if (classes) {
      componentRef.location.nativeElement.classList.add(classes);
    }

    return groupComponent;
  }

  private createStyleComponent(style: ThemeSchemaStyle, container: ViewContainerRef, classes: string = ''): AbstractStyleComponent | null {

    let componentType: any = null;

    switch (style.styleType) {
      case 'background':
        componentType = BackgroundComponent;
        break;
      case 'border':
        componentType = BorderComponent;
        break;
      case 'button':
        componentType = ButtonComponent;
        break;
      case 'color':
        componentType = ColorComponent;
        break;
      case 'font':
        componentType = FontComponent;
        break;
      case 'input':
        componentType = InputComponent;
        break;
      case 'shadow':
        componentType = ShadowComponent;
        break;
      case 'widgetStyle':
        componentType = WidgetStyleComponent;
        break;
      case 'formStyle':
        componentType = FormStyleComponent;
        break;
      case 'step':
        componentType = StepComponent;
        break;
      case 'stepActive':
        componentType = StepActiveComponent;
        break;
      default:
        console.error('Unknown style type: ' + style.styleType);
        return null;
    }

    const componentRef = container.createComponent(componentType);
    const component = componentRef.instance as AbstractStyleComponent;

    component.key = style.key;
    component.label = style.description;

    if (classes) {
      componentRef.location.nativeElement.classList.add(classes);
    }

    if (component.onValueChange) {
      component.onValueChange((key: string, value: any) => {
        if (this.theme) {
          this.theme[key] = value;
        }
      });
    }

    return component;
  }
}
